import { createContext, useContext, useState, ReactNode } from 'react';
import { STORAGE_KEY } from '../constants';
import { AuthUser } from '../api/authApi';

interface AuthContextValue {
  user: AuthUser | null;
  login: (user: AuthUser) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredUser(): AuthUser | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;
    const parsed = JSON.parse(stored) as Partial<AuthUser>;
    if (!parsed || typeof parsed.username !== 'string' || !Array.isArray(parsed.permissions)) {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed as AuthUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(readStoredUser);

  const login = (loggedInUser: AuthUser) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(loggedInUser));
    } catch {
      // ignore storage failures
    }
    setUser(loggedInUser);
  };

  const logout = () => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // ignore storage failures
    }
    setUser(null);
  };

  return <AuthContext.Provider value={{ user, login, logout }}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
